<template>
    <div class="h-full flex flex-col px-4">
        <div class="h-full flex gap-x-2 pb-0 tasks-list-view">
            <div
                ref="tasksListContainer"
                class="relative group w-1/3 shrink-0 overflow-y-auto h-full flex flex-col"
            >
                <loading-bar :class="isLoading ? 'opacity-100' : 'opacity-0'"/>

                <file-uploader
                    v-model="attachments"
                    class="mb-2"
                    short-desc
                    variant="simple"
                    @upload-success="handleAttachmentUploadSuccess"
                />
                <ag-grid-vue
                    id="document-list"
                    cy-data="document-list"
                    :column-defs="columns"
                    :row-data="allDocuments"
                    :grid-options="gridOptions"
                    class="w-full grow outline-none tasks-list-grid"
                    :theme="agTheme"
                    :suppress-header-focus="true"
                    tabindex="0"
                    @cell-key-down="handleKeyDown"
                    @grid-size-changed="onResizeGrid"
                    @first-data-rendered="focusFirstAGCell"
                    @row-data-updated="focusFirstAGCell"
                />
                <div class="flex flex-col justify-center pt-4 pb-2">
                    <span class="text-sm">E-postadresse for innkommende bilag</span>
                    <span>{{ tenantEmailAddress }}</span>
                </div>
            </div>
            <div v-if="selected" class="preview-pane border grow h-full overflow-auto">
                <ehf-raw-view v-if="selected.invoiceDraft && selected.documentMeta.contentType === 'application/json'" :incoming-document="selected">
                    Parsed EHF-viewer: {{ selected.invoiceDraft }}
                </ehf-raw-view>
                <incoming-csv-preview v-else-if="selected.documentMeta.contentType === 'text/csv'" :incoming-document="selected"/>
                <document-preview v-else class="p-1" :document-meta-uuid="selected.documentMeta.uuid"/>
            </div>
            <div v-else class="preview-pane grow h-full flex items-center justify-center">
                <span>Forhåndsvisning av filer</span>
            </div>
        </div>
    </div>
</template>


<script setup lang="ts">
import {agTheme} from '@/agUtils/ag-theme.service'
import LoadingBar from '@/components/LoadingBar.vue'
import DocumentPreview from '@/document/DocumentPreview.vue'
import emitter, {useEmitter} from '@/services/infrastructure/emitter'
import {
    documentMetaDelete,
    documentMetaDeleteBatch,
    documentMetaUpdate,
    INCOMING_DOCUMENT_EVENTS,
    listIncomingDocumentsV2,
    registerIncomingDocuments
} from '@/services/document.service'
import {ATTACHMENT_UPLOADED, ATTACHMENT_UPLOADING, ORG_SWITCHED} from '@/types/events'
import {TRANSACTION_CREATED, TRANSACTION_UPDATED} from '@/types/transaction/TransactionEvent'
import {
    CellFocusedEvent,
    CellKeyDownEvent,
    ColDef,
    GetRowIdParams,
    GridApi,
    GridOptions,
    GridReadyEvent,
    GridSizeChangedEvent,
    ICellRendererParams,
    IRowNode,
    ITooltipParams,
    KeyCode,
    RowSelectedEvent,
    ValueGetterParams,
    ValueSetterParams
} from 'ag-grid-community'
import {AgGridVue} from 'ag-grid-vue3'
import {computed, onMounted, onUnmounted, ref, shallowRef} from 'vue'
import {formatCurrency, formattedDate} from '@/services/formatter.service'
import DocumentButtonsCellRenderer from '@/document/DocumentButtonsCellRenderer.vue'
import {DocumentMeta} from '@/types/transaction/DocumentMeta'
import {useTransactionCreator} from '@/transaction/create-transaction/TransactionCreator.service'
import FileUploader from '@/document/FileUploader.vue'
import {HTMLDivElement} from 'happy-dom'
import TaskNameCell from '@/task-manager/TaskNameCell.vue'
import {IncomingDocument} from '@/types/IncomingDocument'
import TaskManagerSelectionCell from '@/task-manager/TaskManagerSelectionCell.vue'
import {LIST_DOCUMENTS} from '@/task-manager/task.events'
import {InvoiceDraft, InvoiceDraftLine} from '@/invoice/types/InvoiceDraft'
import EhfRawView from '@/document/EhfRawView.vue'
import {notifyError, notifySuccess} from '@/services/infrastructure/notification.service'
import IncomingCsvPreview from '@/document/IncomingCsvPreview.vue'
import {useTaskManager} from '@/task-manager/taskManager.service'
import DocumentHeaderButton from '@/document/DocumentHeaderButton.vue'
import {useAttachDocumentService} from '@/transaction/create-transaction/AttachDocument.service'
import {PatronOverview} from '@tritt/patron-client-ts'
import {getEnvironment} from '@/services/infrastructure/envhelper.service'
import {selectedOrg} from '@/tenants/org.service'
import {fetchPatronOverview} from '@/services/infrastructure/patron.service'
import _ from 'lodash'

const {attachments} = useTransactionCreator()
const {onEvent, cleanUp, onEvents} = useEmitter()
const {detachDocument, detachDocuments} = useAttachDocumentService()
const {panelAwareToggleDocumentAttach} = useTaskManager()

const selectedUuid = ref()

const isLoading = ref(false)
const gridApi = shallowRef<GridApi>()
const allDocuments = ref<IncomingDocument[]>([])
const deleteConfirmationState = ref<Record<string, boolean>>({})

const tasksListContainer = ref<HTMLDivElement | null>(null)

const selected = computed(() => {
    return allDocuments.value.find(file => file.documentMeta.uuid === selectedUuid.value)
})

const patronOverview = ref<PatronOverview>()

const tenantEmailAddress = computed(() => {
    if (!patronOverview.value) {
        return ''
    }
    const hri = patronOverview.value.generatedHri
    const environment = getEnvironment()
    if (environment === 'staging') {
        return hri + '@staging.contabilag.no'
    }
    if (environment === 'preproduction') {
        return hri + '@preprod.contabilag.no'
    }
    if (environment === 'production') {
        return hri + '@pro.contabilag.no'
    }
    return hri + '@test.no'
})

const focusFirstAGCell = () => gridApi.value?.setFocusedCell(0, 'selection')

const handleKeyDown = async (ckde: CellKeyDownEvent) => {
    const kbEvent = ckde.event as unknown as KeyboardEvent
    if (!ckde.event || !kbEvent.key) return
    switch (kbEvent.key) {
        case KeyCode.SPACE: {
            const cellPosition = ckde.node.rowIndex !== null ? {rowIndex: ckde.node.rowIndex, column: ckde.column, rowPinned: undefined} : null
            const isEditing = cellPosition && ckde.api.isEditing(cellPosition)
            if(!isEditing) {
                await panelAwareToggleDocumentAttach(ckde.node.data)
            }
        }
    }
}

const refreshFiles = async () => {
    isLoading.value = true
    allDocuments.value = await listIncomingDocumentsV2()
    isLoading.value = false
    if (allDocuments.value.length > 0 && !selectedUuid.value && gridApi.value) {
        const firstRow = gridApi.value.getDisplayedRowAtIndex(0)
        if (!firstRow) return
        gridApi.value.setNodesSelected({nodes: [firstRow], newValue: true})
    }
}

const debouncedRefreshFiles = _.debounce(refreshFiles, 500,{trailing: true})

const refreshPatron = async () => {
    if (selectedOrg.value) {
        patronOverview.value = await fetchPatronOverview(selectedOrg.value.organizationId)
    }
}

const showDeleteConfirmation = (key: string) => {
    deleteConfirmationState.value[key] = true
}

const hideDeleteConfirmation = (key: string) => {
    deleteConfirmationState.value[key] = false
}

const deleteIncomingDocument = async (incomingDocument: IncomingDocument) => {
    const documentMeta = incomingDocument.documentMeta

    try {
        await documentMetaDelete(documentMeta.uuid)
        detachDocument(incomingDocument)
        emitter.emit(LIST_DOCUMENTS)
        hideDeleteConfirmation(documentMeta.uuid)
    } catch (error) {
        notifyError({title: 'Feilet', description: {descriptionMessage: `Klarte ikke å slette dokumentet med id: ${documentMeta.uuid}`, error: error}})
    }
}

const deleteIncomingDocuments = async () => {
    const uuidsToDelete = new Set(attachments.value.map(doc => doc.uuid))
    const incomingDocuments = allDocuments.value.filter(doc =>
        uuidsToDelete.has(doc.documentMeta.uuid)
    )

    const uuidArray = Array.from(uuidsToDelete)

    try {
        await documentMetaDeleteBatch(uuidArray)
        detachDocuments(incomingDocuments)
        emitter.emit(LIST_DOCUMENTS)
    } catch (error) {
        notifyError({
            title: 'Feilet',
            description: {
                descriptionMessage: 'Klarte ikke å slette ett eller flere dokumenter.',
                error: error
            }
        })
    }
}

const getVatSummary = (invoiceDraftLines: InvoiceDraftLine[]) => {
    const vatRates = new Set(invoiceDraftLines.map(line => line.vatRate))
    if (vatRates.size === 1) {
        return Array.from(vatRates)[0]
    }
    return ''
}

const isCreditNote = (invoiceDraft: InvoiceDraft): Boolean => {
    if (invoiceDraft.draft && invoiceDraft.draft.grossAmount) {
        return invoiceDraft.draft.grossAmount < 0
    }
    return false
}

const handleAttachmentUploadSuccess = async (docs: DocumentMeta[]) => {
    await registerIncomingDocuments(docs)
    debouncedRefreshFiles()
}

// Grid and columns
const columns = computed<ColDef[]>(() => [
    {
        colId: 'selection',
        width: 42,
        maxWidth: 42,
        minWidth: 42,
        suppressHeaderMenuButton: true,
        cellRenderer: TaskManagerSelectionCell,
    },
    {
        colId: 'name',
        width: 150,
        editable: true,
        headerName: 'Navn',
        cellRendererSelector: ({data}: ICellRendererParams) => {
            if (data.invoiceDraft.draft.sender?.name) {
                const invoiceType = isCreditNote(data.invoiceDraft) ? 'Kreditnota' : 'Faktura'
                return `${invoiceType} fra ${data.invoiceDraft.draft.sender.name}`
            }
            return {component: TaskNameCell}
        },
        valueGetter: ({data}: ValueGetterParams) => {
            if (data.invoiceDraft.draft.sender?.name) {
                const invoiceType = isCreditNote(data.invoiceDraft) ? 'Kreditnota' : 'Faktura'
                return `${invoiceType} fra ${data.invoiceDraft.draft.sender.name}`
            }
            return data.documentMeta.filename
        },
        valueSetter: async ({data, newValue}: ValueSetterParams) => {
            try {
                const newData = await documentMetaUpdate(({...data, uuid: data.documentMeta.uuid, filename: newValue}))
                const {uuid, filename} = ((newData as any)?.value as DocumentMeta)

                const attachmentIndex = attachments.value.findIndex(item => item.uuid === uuid)
                if (attachmentIndex !== -1) {
                    attachments.value[attachmentIndex].filename = filename
                }

                await debouncedRefreshFiles()
                notifySuccess({title: 'Oppdatert', description: 'Filnavnet ble oppdatert.'})
            } catch (err) {
                notifyError({title: 'Feilet', description: 'Filnavnet kunne ikke endres.'})
            }
        },
        tooltipValueGetter: ({data}: ITooltipParams) => {
            if (data.invoiceDraft.draft.sender?.name) {
                const invoiceType = isCreditNote(data.invoiceDraft) ? 'Kreditnota' : 'Faktura'
                return `${invoiceType} fra ${data.invoiceDraft.draft.sender.name}`
            }
            return data.documentMeta.filename
        }
    },
    {
        headerName: 'Beløp',
        sortable: false,
        width: 70,
        cellStyle: {'text-align': 'right'},
        headerClass: 'header-right',
        cellRenderer: ({data}: ICellRendererParams) => {
            if (data.invoiceDraft.draft.grossAmount) {
                return formatCurrency(data.invoiceDraft.draft.grossAmount) + ' kr'
            } else {
                return ''
            }
        },
        colId: 'amount'
    },
    {
        headerName: 'MVA',
        colId: 'vat',
        width: 90,
        cellStyle: {'text-align': 'right'},
        headerClass: 'header-right',
        cellRenderer: ({value}: ICellRendererParams) => {
            if (value) {
                return value * 100 + '%'
            } else {
                return ''
            }
        },
        valueGetter: ({data}: ValueGetterParams) => {
            return getVatSummary(data.invoiceDraft?.draft?.productLines || [])
        }
    },
    {
        headerName: 'Forfall',
        width: 90,
        cellRenderer: ({data}: ICellRendererParams) => {
            if (data.invoiceDraft.draft.dueDate) {
                return formattedDate(data.invoiceDraft.draft.dueDate)
            } else {
                return ''
            }
        },
        colId: 'dueDate'
    },
    {
        headerName: 'Dato',
        width: 90,
        cellRenderer: ({data}: ICellRendererParams) => {
            if (data.invoiceDraft.draft.date) {
                return formattedDate(data.invoiceDraft.draft.date)
            }
            return formattedDate(data.documentMeta.createdAt)
        },
        colId: 'createdDate'
    },
    {
        headerName: 'Beskrivelse',
        width: 200,
        cellRenderer: () => {
            return ''
        },
        colId: 'description'
    },
    {
        sortable: false,
        suppressMovable: true,
        headerComponent: DocumentHeaderButton,
        cellRenderer: DocumentButtonsCellRenderer,
        width: 120,
        colId: 'documentButtons'
    }
] as ColDef[])

const onResizeGrid = (params: GridSizeChangedEvent) => {
    const xLargeGrid = ['fileIcon', 'name', 'amount', 'vat', 'description', 'dueDate', 'createdDate', 'documentButtons']
    const largeGrid = ['fileIcon', 'name', 'amount', 'vat', 'dueDate', 'description', 'documentButtons']
    const mediumGrid = ['fileIcon', 'name', 'amount', 'vat', 'dueDate', 'documentButtons']
    const smallGrid = ['name', 'amount', 'documentButtons']
    const xSmallGrid = ['name', 'documentButtons']

    const gridWidth = params.clientWidth

    const columnsToShow: Array<string> = []
    const columnsToHide: Array<string> = []

    if (gridWidth > 800) {
        columnsToShow.push(...xLargeGrid)
    } else if (gridWidth > 700) {
        columnsToShow.push(...largeGrid)
        columnsToHide.push(...xLargeGrid.filter(colId => !largeGrid.includes(colId)))
    } else if (gridWidth > 500) {
        columnsToShow.push(...mediumGrid)
        columnsToHide.push(...xLargeGrid.filter(colId => !mediumGrid.includes(colId)))
    } else if (gridWidth > 300) {
        columnsToShow.push(...smallGrid)
        columnsToHide.push(...xLargeGrid.filter(colId => !smallGrid.includes(colId)))
    } else {
        columnsToShow.push(...xSmallGrid)
        columnsToHide.push(...xLargeGrid.filter(colId => !xSmallGrid.includes(colId)))
    }

    params.api.setColumnsVisible(columnsToShow, true)
    params.api.setColumnsVisible(columnsToHide, false)

    params.api.sizeColumnsToFit()
}

const gridOptions: GridOptions = {
    defaultColDef: {
        suppressKeyboardEvent: (params) => {
            return params.event.key === KeyCode.SPACE
        },
    },
    animateRows: true,
    rowSelection: {
        mode: 'singleRow',
        enableClickSelection: true,
        checkboxes: false
    },
    cellSelection: false,
    onCellFocused: (event: CellFocusedEvent) => {
        if (event.rowIndex === null) return
        const row: IRowNode<IncomingDocument> | undefined = event.api.getDisplayedRowAtIndex(event.rowIndex)
        if (!row) return
        event.api.setNodesSelected({nodes: [row], newValue: true})
    },
    onRowSelected(event: RowSelectedEvent<IncomingDocument>) {
        if (event.node.isSelected()){
            selectedUuid.value = event.data?.documentMeta.uuid
        }
    },
    getRowId: (params: GetRowIdParams<IncomingDocument>): string => params.data?.documentMeta.uuid,
    onGridReady: (event: GridReadyEvent) => {
        gridApi.value = event.api
        window.gridApi = event.api
    },
    suppressContextMenu: true,
    overlayNoRowsTemplate: '<span>Ingen filer funnet</span>',
    context: {
        showDeleteConfirmation,
        hideDeleteConfirmation,
        deleteConfirmationState,
        deleteIncomingDocument,
        deleteIncomingDocuments,
    }
}

// Lifecycle methods
onMounted(() => {
    isLoading.value = true
    debouncedRefreshFiles()
    refreshPatron()
    // TODO cleanup amount of events interested
    onEvents([...INCOMING_DOCUMENT_EVENTS, ATTACHMENT_UPLOADED, ORG_SWITCHED, TRANSACTION_CREATED, TRANSACTION_UPDATED, LIST_DOCUMENTS], debouncedRefreshFiles)
    onEvent(ORG_SWITCHED, refreshPatron)
    onEvent(TRANSACTION_CREATED, focusFirstAGCell)
    onEvent(ATTACHMENT_UPLOADING, () => isLoading.value = true)
    onEvent(ATTACHMENT_UPLOADED, () => isLoading.value = false)
    isLoading.value = false
})

onUnmounted(cleanUp)

</script>


<style>
@reference "@/index.css";
@reference "tailwindcss";

.tasks-list-view {
    #document-list {
        /* TODO: Colors are copied from rendered CreateTransaction, but without the transparency from bg-primary/20. Colours should probably be extracted and standardized
             Also, all of this is shit, we're juggling states and implementing bad UX here IMO. a checkbox should not cause attachment. Need proper UX here!
             */

        .ag-row, .ag-row-selected, .ag-row-hover, .ag-cell {
            background-color: white;

            [data-theme="dark"] & {
                background-color: #2c2f2b;
            }
        }

        .ag-row.ag-row-selected {
            .ag-cell:not([col-id="selection"]) {
                background-color: #d6d6d6;

                [data-theme="dark"] & {
                    background-color: #bababa;
                }
            }

            &:has(.ag-cell:focus-within) {
                .ag-cell:not([col-id="selection"]) {
                    background-color: #d2e0d5;
                    border-color: transparent;

                    [data-theme="dark"] & {
                        background-color: #536152;
                    }
                }
            }
        }
    }
}

</style>